import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsCertificates = ({ onNavigate, userInfo }) => {

  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountsData, setAccountsData] = useState({
    name: "Loading...",
    accountsId: "...",
    department: "Accounts Department",
    designation: "Accounts Officer",
    profileImage: null
  });

  // Certificates Data States
  const [certificatesData, setCertificatesData] = useState({
    totalCertificates: 0,
    pendingCertificates: 0,
    completedCertificates: 0,
    certificates: []
  });

  // PDF Generation States
  const [generatingPDF, setGeneratingPDF] = useState({});
  const [issuingCertificate, setIssuingCertificate] = useState({});

  // Fetch Certificates Data from API
  useEffect(() => {
    const fetchCertificatesData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch accounts certificates data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        console.log('🔑 Token found:', token ? 'Yes' : 'No');

        if (!token) {
          console.log('❌ No token found, using fallback data');
          setError('Authentication required. Please login again.');
          setAccountsData(prev => ({
            ...prev,
            name: userInfo?.name || "Accounts User",
            accountsId: userInfo?.id || "ACC2024001"
          }));
          setLoading(false);
          return;
        }

        // Construct the full API URL
        const apiUrl = `${API_LINK}/api/accounts/certificates`;
        console.log('🌐 API URL:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            handleLogout();
            return;
          } else if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('❌ Response is not JSON:', contentType);
          setError('Server returned non-JSON response.');
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('📦 Full API Response:', result);

        if (result.success && result.data) {
          const data = result.data;
          console.log('✅ Processing certificates data...');

          // Update accounts data
          setAccountsData({
            name: data.accounts?.name || userInfo?.name || "Accounts Officer",
            accountsId: data.accounts?.accountsId || userInfo?.id || "ACC2024001",
            department: data.accounts?.department || "Accounts Department",
            designation: data.accounts?.designation || "Accounts Officer",
            profileImage: data.accounts?.profileImage || null
          });

          // Update certificates data
          setCertificatesData({
            totalCertificates: data.stats?.totalCertificates || 0,
            pendingCertificates: data.stats?.pendingCertificates || 0,
            completedCertificates: data.stats?.completedCertificates || 0,
            certificates: data.certificates || []
          });

          console.log('✅ Certificates data updated successfully');
        } else {
          console.log('❌ API returned error:', result.message);
          setError(result.message || 'Failed to fetch certificates data');
        }

      } catch (error) {
        console.error('💥 Error fetching certificates data:', error);
        setError(`Failed to load certificates data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchCertificatesData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // ============================================
  // PDF GENERATION FUNCTIONS
  // ============================================

  /**
   * Generate PDF for a single certificate
   */
  const handleGeneratePDF = async (certificateId, certificateNumber) => {
    try {
      console.log('📄 Generating PDF for certificate:', certificateId);

      // Set generating state
      setGeneratingPDF(prev => ({ ...prev, [certificateId]: true }));

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(
        `${API_LINK}/api/accounts/certificates/${certificateId}/generate-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        console.log('✅ PDF generated successfully:', result.data);

        // Auto-download the generated PDF
        console.log('📥 Auto-downloading PDF...');
        await handleDownloadPDF(certificateId, certificateNumber);

        // Update the certificates list to show download button
        // Fetch updated certificate data
        const certResponse = await fetch(
          `${API_LINK}/api/accounts/certificates`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const certResult = await certResponse.json();

        if (certResult.success && certResult.data) {
          setCertificatesData({
            totalCertificates: certResult.data.stats?.totalCertificates || 0,
            pendingCertificates: certResult.data.stats?.pendingCertificates || 0,
            completedCertificates: certResult.data.stats?.completedCertificates || 0,
            certificates: certResult.data.certificates || []
          });
        }

        alert(`Certificate PDF generated and downloaded!\nCertificate: ${result.data.certificateNumber}`);
      } else {
        console.error('❌ Failed to generate PDF:', result.message);
        alert(`Failed to generate PDF: ${result.message}`);
      }

    } catch (error) {
      console.error('💥 Error generating PDF:', error);
      alert(`Error generating PDF: ${error.message}`);
    } finally {
      setGeneratingPDF(prev => ({ ...prev, [certificateId]: false }));
    }
  };

  /**
   * Download PDF for a certificate
   */
  const handleDownloadPDF = (certificateId, certificateNumber) => {
    console.log('📥 Downloading PDF for certificate:', certificateId);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    // Open download in new tab
    const downloadUrl = `${API_LINK}/api/accounts/certificates/${certificateId}/download-pdf`;

    // Create a temporary link to trigger download
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Certificate_${certificateNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        console.log('✅ PDF download initiated');
      })
      .catch(error => {
        console.error('❌ Error downloading PDF:', error);
        alert('Error downloading PDF. Please try again.');
      });
  };

  /**
   * Issue Certificate - Updates status to approved and generates PDF
   */
  const handleIssueCertificate = async (certificateId, certificateNumber) => {
    try {
      const confirmIssue = window.confirm(
        `Are you sure you want to issue certificate: ${certificateNumber}?\n\n` +
        `This will:\n` +
        `• Change status to APPROVED\n` +
        `• Generate PDF certificate\n` +
        `• Set issue date to today`
      );

      if (!confirmIssue) return;

      console.log('✅ Issuing certificate:', certificateId);

      // Set issuing state
      setIssuingCertificate(prev => ({ ...prev, [certificateId]: true }));

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(
        `${API_LINK}/api/accounts/certificates/${certificateId}/issue`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            approvedBy: accountsData.name || 'Accounts Officer',
            verifiedBy: accountsData.name || 'Accounts Officer',
            remarks: 'Certificate issued and approved'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        console.log('✅ Certificate issued successfully:', result.data);

        // Auto-download the generated PDF
        console.log('📥 Auto-downloading PDF...');
        await handleDownloadPDF(certificateId, certificateNumber);

        // Update the certificates list to show new status and download button
        const certResponse = await fetch(
          `${API_LINK}/api/accounts/certificates`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const certResult = await certResponse.json();

        if (certResult.success && certResult.data) {
          // Update both stats and certificates list
          setAccountsData(prev => ({
            ...prev,
            name: certResult.data.accounts?.name || prev.name,
            accountsId: certResult.data.accounts?.accountsId || prev.accountsId,
            department: certResult.data.accounts?.department || prev.department,
            designation: certResult.data.accounts?.designation || prev.designation,
            profileImage: certResult.data.accounts?.profileImage || prev.profileImage
          }));

          setCertificatesData({
            totalCertificates: certResult.data.stats?.totalCertificates || 0,
            pendingCertificates: certResult.data.stats?.pendingCertificates || 0,
            completedCertificates: certResult.data.stats?.completedCertificates || 0,
            certificates: certResult.data.certificates || []
          });
        }

        alert(
          `Certificate Issued Successfully!\n\n` +
          `Certificate: ${result.data.certificateNumber}\n` +
          `Status: APPROVED\n` +
          `PDF Generated and Downloaded!`
        );
      } else {
        console.error('❌ Failed to issue certificate:', result.message);
        alert(`Failed to issue certificate: ${result.message}`);
      }

    } catch (error) {
      console.error('💥 Error issuing certificate:', error);
      alert(`Error issuing certificate: ${error.message}`);
    } finally {
      setIssuingCertificate(prev => ({ ...prev, [certificateId]: false }));
    }
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Certificate Management...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '3rem', color: '#f44336' }}>⚠️</div>
        <p style={{ color: '#666', fontSize: '1.1rem', textAlign: 'center', maxWidth: '400px' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#238548',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Layout role="accounts"
      onNavigate={onNavigate}
      activePage="accounts-certificates"
      pageTitle="Work Completion Certificates"
      accountsData={accountsData}
    >
      {/* Page Header */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          margin: '0 0 1rem 0',
          color: '#333',
          fontSize: '2rem',
          fontWeight: 'bold'
        }}>
          📜 Work Completion Certificate Management
        </h1>
        <p style={{
          margin: 0,
          color: '#666',
          fontSize: '1.1rem',
          lineHeight: 1.6
        }}>
          Manage work completion certificates, track certificate requests, and oversee certificate issuance process.
        </p>
      </div>

      {/* Stats Cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Total Certificates Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>📜</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Total Certificates</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {certificatesData.totalCertificates > 0 ? certificatesData.totalCertificates : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            All Time
          </p>
        </div>

        {/* Pending Certificates Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>⏳</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Pending Certificates</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {certificatesData.pendingCertificates > 0 ? certificatesData.pendingCertificates : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Awaiting Approval
          </p>
        </div>

        {/* Completed Certificates Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>✅</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Completed Certificates</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {certificatesData.completedCertificates > 0 ? certificatesData.completedCertificates : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            This Month
          </p>
        </div>
      </section>

      {/* Certificates List */}
      <section style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span style={{ fontSize: '1.8rem' }}>📋</span>
          <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Certificate Requests</h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {certificatesData.certificates.length > 0 ? certificatesData.certificates.map((certificate, index) => (
            <div key={index} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '1rem',
              transition: 'all 0.3s ease',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '0.5rem' }}>
                  {certificate.employeeName}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Project: {certificate.projectName}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Requested: {formatDate(certificate.requestDate)}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{
                  padding: '0.5rem 1rem',
                  background: certificate.status === 'completed' ? '#e8f5e9' : certificate.status === 'pending' ? '#fff3cd' : '#ffebee',
                  color: certificate.status === 'completed' ? '#2e7d32' : certificate.status === 'pending' ? '#856404' : '#c62828',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  textTransform: 'capitalize'
                }}>
                  {certificate.status}
                </div>

                {/* Issue Certificate Button - Only for pending/draft certificates */}
                {(certificate.status === 'pending' || certificate.status === 'draft' || certificate.status === 'in-progress' || certificate.status === 'verified') && (
                  <button
                    onClick={() => handleIssueCertificate(certificate.id, certificate.certificateNumber)}
                    disabled={issuingCertificate[certificate.id]}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: issuingCertificate[certificate.id] ? '#cccccc' : '#238548',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: issuingCertificate[certificate.id] ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {issuingCertificate[certificate.id] ? '⏳ Issuing...' : '✅ Issue Certificate'}
                  </button>
                )}

                {/* Generate PDF Button - Only for approved certificates without PDF */}
                {(certificate.status === 'approved' || certificate.status === 'completed' || certificate.status === 'closed') && !certificate.certificateFile && (
                  <button
                    onClick={() => handleGeneratePDF(certificate.id, certificate.certificateNumber)}
                    disabled={generatingPDF[certificate.id]}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: generatingPDF[certificate.id] ? '#cccccc' : '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: generatingPDF[certificate.id] ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {generatingPDF[certificate.id] ? '⏳ Generating...' : '📄 Generate PDF'}
                  </button>
                )}

                {/* Download PDF Button - Only if PDF exists */}
                {certificate.certificateFile && (
                  <button
                    onClick={() => handleDownloadPDF(certificate.id, certificate.certificateNumber)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    📥 Download PDF
                  </button>
                )}

                <button
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#238548',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  View Details
                </button>
              </div>
            </div>
          )) : (
            <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
              No certificate requests available
            </p>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default AccountsCertificates;